import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DownloadSimple, Newspaper, Buildings, ChartLineUp, Globe, Quotes, ArrowUpRight } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import useSeo from "@/lib/useSeo";

const MILESTONES = [
  { year: "2024", title: "Company founded", desc: "IslandHop Technologies LLC incorporated in the United States." },
  { year: "2025 Q1", title: "Platform architecture", desc: "Core multi-service logistics platform designed for the Caribbean market." },
  { year: "2025 Q3", title: "Beta build", desc: "Mobile app build, driver onboarding flow and merchant dashboard prototyped." },
  { year: "2025 Q4", title: "Pre-launch", desc: "Public marketing site, driver & merchant waitlist open across Trinidad & Tobago." },
  { year: "2026", title: "Trinidad & Tobago launch", desc: "Phase 1 launch — delivery, handyman and cleaning across major T&T cities. Beyond: tech support, events and regional expansion." },
];

const FACTS = [
  { icon: Globe, label: "Region", value: "Caribbean", sub: "T&T HQ · regional expansion" },
  { icon: Buildings, label: "Entity", value: "U.S. LLC", sub: "Delaware-registered" },
  { icon: ChartLineUp, label: "Revenue model", value: "4 streams", sub: "Fees · commission · platform · subs" },
  { icon: Newspaper, label: "Stage", value: "Pre-launch", sub: "Open to investors & merchants" },
];

export default function Press() {
  useSeo({
    title: "Press & Investors",
    description: "Media kit, milestones, company facts and investor contacts for IslandHop Technologies LLC.",
  });

  return (
    <div data-testid="press-page">
      {/* HERO */}
      <section className="relative pt-20 lg:pt-28 pb-16 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-40 [mask-image:linear-gradient(180deg,#000,transparent)]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[500px] ih-radial-blue" />

        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-8 space-y-6">
            <SectionEyebrow label="Press & Investors" color="electric" />
            <h1 className="font-display font-black text-5xl lg:text-7xl text-white leading-[0.95] text-balance">
              The story of <br />
              <span className="text-white/40">a Caribbean</span> <span className="text-ih-electric">logistics platform.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-2xl leading-relaxed">
              Media inquiries, investor conversations, partnership opportunities — everything you need to
              understand IslandHop is on this page.
            </p>
          </motion.div>

          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <a
              href="#media-kit"
              data-testid="press-media-kit-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ih-electric text-white font-semibold hover:scale-[1.02] transition ih-shadow-blue"
            >
              <DownloadSimple weight="bold" size={16} /> Media kit
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-ih-line2 hover:border-white/30 transition">
              Investor inquiries <ArrowUpRight weight="bold" size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FACT GRID */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {FACTS.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="bg-ih-surface border border-ih-line rounded-2xl p-6"
                data-testid={`press-fact-${i}`}
              >
                <Icon weight="duotone" size={22} className="text-ih-electric mb-4" />
                <p className="text-xs uppercase tracking-[0.16em] text-white/40">{f.label}</p>
                <p className="font-display font-extrabold text-2xl text-white mt-1">{f.value}</p>
                <p className="text-xs text-white/50 mt-1">{f.sub}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* COMPANY OVERVIEW */}
      <section className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-24 self-start space-y-5">
            <SectionEyebrow label="Company overview" color="neon" />
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-white leading-[1.05]">
              For media,<br />
              investors &amp; <span className="text-ih-neon">policymakers.</span>
            </h2>
          </div>

          <div className="lg:col-span-7 space-y-6 text-white/75 leading-relaxed text-lg">
            <p>
              <span className="text-white font-semibold">IslandHop Technologies LLC</span> is a U.S.-registered technology
              company building a multi-service on-demand platform serving Trinidad &amp; Tobago and the wider
              Caribbean region.
            </p>
            <p>
              The company operates a mobile application that connects customers with independent contractors,
              service professionals and merchants across five categories:
              <span className="text-white"> delivery</span>,
              <span className="text-white"> handyman</span>,
              <span className="text-white"> cleaning</span>,
              <span className="text-white"> tech support</span> and
              <span className="text-white"> events</span>. IslandHop runs entirely online with no physical storefront,
              leveraging a network of vetted local pros for fulfillment.
            </p>
            <p>
              Revenue is generated across four streams: <span className="text-white">delivery service fees</span>,
              <span className="text-white"> merchant commission (10–25%)</span>,
              <span className="text-white"> platform usage fees</span>, and
              <span className="text-white"> subscription plans for business merchants</span>. The platform is built for
              scale, with a roadmap that extends into fintech, AI-powered dispatch and regional logistics infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="max-w-2xl space-y-5 mb-12">
            <SectionEyebrow label="Milestones" color="orange" />
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-white leading-[1.05]">
              From idea to <span className="text-ih-orange">infrastructure.</span>
            </h2>
          </div>

          <div className="relative space-y-3">
            <div className="absolute left-5 top-0 bottom-0 w-px bg-ih-line2 hidden md:block" />
            {MILESTONES.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="relative bg-ih-surface border border-ih-line rounded-2xl p-6 md:pl-16"
                data-testid={`milestone-${i}`}
              >
                <span className="hidden md:grid absolute left-2 top-6 w-6 h-6 rounded-full bg-ih-ink border border-ih-orange place-items-center text-[10px] font-mono text-ih-orange">{i + 1}</span>
                <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6">
                  <p className="font-mono text-xs uppercase tracking-[0.18em] text-ih-orange shrink-0 md:w-24">{m.year}</p>
                  <div>
                    <p className="font-display font-bold text-xl text-white">{m.title}</p>
                    <p className="text-white/60 mt-1">{m.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE / MEDIA KIT */}
      <section id="media-kit" className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 bg-ih-surface border border-ih-line rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <Quotes size={42} weight="fill" className="text-ih-neon mb-6" />
            <p className="font-display font-bold text-2xl lg:text-3xl text-white leading-snug max-w-2xl">
              &ldquo;The Caribbean deserves the same delivery infrastructure as New York or São Paulo —
              built locally, operated by people who live here, and engineered to scale across the region.&rdquo;
            </p>
            <p className="mt-6 text-sm font-mono uppercase tracking-[0.18em] text-white/50">
              IslandHop founding team
            </p>
          </div>

          <div className="lg:col-span-5 bg-ih-surface border border-ih-line rounded-3xl p-8 lg:p-10 space-y-5">
            <h3 className="font-display font-extrabold text-2xl text-white">Media kit</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Logos, brand colors, photos and the company one-pager. For interview requests please reach out
              directly via the contact page.
            </p>
            <ul className="space-y-2">
              {[
                "Logo pack (SVG · PNG)",
                "Brand guidelines",
                "Founder photos",
                "Product screenshots",
                "Company one-pager (PDF)",
              ].map((label) => (
                <li key={label} data-testid={`media-kit-item-${label.toLowerCase().split(" ")[0]}`} className="flex items-center justify-between bg-black/40 border border-ih-line2 rounded-xl px-4 py-3">
                  <span className="text-sm text-white/80">{label}</span>
                  <span className="text-xs text-ih-neon font-mono">Coming Soon</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="inline-flex items-center gap-2 text-sm text-ih-electric underline underline-offset-4">
              Request the media kit <ArrowUpRight weight="bold" size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
          <h3 className="font-display font-extrabold text-3xl lg:text-4xl text-white max-w-xl">
            Investors, journalists, partners — <span className="text-ih-electric">let&apos;s talk.</span>
          </h3>
          <Link to="/contact" data-testid="press-contact-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-black font-semibold hover:scale-[1.02] transition">
            Contact IslandHop <ArrowUpRight weight="bold" size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
